/**
 * «Документи» — реєстр із переглядачем: ліворуч список, праворуч документ.
 *
 * КАРКАС за референсом замовника (master-detail), деталі — за дизайн-контрактом
 * кабінету: скляні плитки .tile, контроли 32/radius 8 з обводкою --line-strong,
 * нейтральний активний стан без брендового червоного (§6), розділення лінією,
 * глибина тінню (§3). Тобто взято КОМПОЗИЦІЮ референсу, а не його оформлення:
 * там активний рядок і кнопка завантаження червоні, у нас червоний лишається
 * за реальною помилкою.
 *
 * ЩО РЕАЛЬНО ПРАЦЮЄ, А ЩО НІ — головне, що треба знати про цей розділ.
 *
 * Нормативні акти мають посилання на zakon.rada.gov.ua, і я перевірив: той
 * сайт не віддає ні X-Frame-Options, ні CSP frame-ancestors, тож його сторінки
 * вбудовуються в кабінет напряму. Для 21 акта переглядач справжній.
 *
 * ФАЙЛИ. Замовник надав сім документів по СЕС «Еліос Лучки» (public/docs):
 * чотири повідомлення Укренерго, два договори й перелік ТКО. Вони прив'язані
 * саме до цієї станції: під іншою обраною СЕС позиція лишається в переліку,
 * але НЕАКТИВНОЮ. Активні рядки — тільки ті, що мають джерело: 28 із 63 за
 * станом на аудит (21 акт із посиланням + 7 файлів по Лучках).
 * Решта позицій документа поки не має.
 *
 * ФІЛЬТР ПЕРІОДУ (Усі / Місячні / Квартальні / Річні) показується ЛИШЕ на
 * вкладці «Звітність»: періодичність є тільки в неї. На інших вкладках цей
 * ряд був би контролом, який нічого не фільтрує.
 *
 * СОРТУВАННЯ — за алфавітом, а не «спочатку нові», як на референсі: дати є
 * лише в тих семи документів, що мають файл, тож сортування за датою
 * розкидало б решту реєстру випадково.
 *
 * ДОДАТИ ДОКУМЕНТ — один рядок у documentsData.ts; інструкція у шапці того
 * файла. Рік, дата в реквізиті й кнопка року в фільтрі рахуються з поля
 * date самі.
 */
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import i18n from '@/i18n';
import DashboardLayout from '@/components/feature/DashboardLayout';
import SegmentedTabs from '@/components/base/SegmentedTabs';
import {
  Scale, FileSignature, FileBarChart, Wrench, ExternalLink, Download, Eye,
  Factory, Search, ArrowDownAZ, ArrowUpAZ, FileText, Loader2,
  type LucideIcon,
} from 'lucide-react';
import { useDocsUi, type DocsTabId } from '@/hooks/useDocsUi';
import { useSelectedAsset } from '@/hooks/useSelectedAsset';
import {
  LEGAL, CONTRACTS, REPORTS, TECHNICAL, docYear, docDateLabel,
  type DocScope,
} from './documentsData';

type TabId = DocsTabId;

/*
 * НАЗВА СТАНЦІЇ береться з селектора ЯК Є, без жодних префіксів.
 *
 * Спершу код дописував «СЕС» — виходило «Реєстр СЕС СЕС ЛУЧКИ», бо селектор
 * віддає назву вже з префіксом. Потім я додав перевірку `/^сес\b/i`, і вона
 * теж не спрацювала: у JavaScript межа слова \b рахується за латиницею
 * (\w = [A-Za-z0-9_]), тож після кириличного «сес» її просто немає, умова
 * завжди хибна, префікс додавався далі.
 *
 * Мораль: не добудовувати назву, яку джерело вже віддало повною.
 */

/**
 * Чи належить файл обраній станції.
 *
 * Назви розходяться між джерелами: селектор показує «СЕС ЛУЧКИ», у даних
 * ключ «лучки». Тому звіряємо підрядком у нижньому регістрі, а не рівністю.
 *
 * Якщо файл прив'язаний до станції, а обрана інша — файла НЕМАЄ. Позиція в
 * переліку лишається: номенклатура однакова для всіх СЕС, а документ по
 * цьому об'єкту просто не завантажений.
 */
const fileForStation = (
  file?: string, station?: string, assetName?: string,
): string | undefined => {
  if (!file) return undefined;
  if (!station) return file;
  return assetName && assetName.toLowerCase().includes(station) ? file : undefined;
};

/**
 * Порядок вкладок — від свого до чужого.
 *
 * Спершу документи станції, якими користуються щодня: договори, звітність,
 * технічна документація. Нормативні акти в кінці: це зовнішня довідкова база,
 * до якої звертаються рідше, ніж до власних договорів, та й лежить вона не в
 * нас, а на zakon.rada.gov.ua.
 *
 * Перша вкладка в ряду є і типовою (див. useDocsUi) — інакше розділ
 * відкривався б не на тій, що стоїть першою.
 */
const TABS: Array<{ id: TabId; label: string; Icon: LucideIcon; count: number }> = [
  { id: 'contracts', get label() { return i18n.t('ak_docspage_contracts'); }, Icon: FileSignature, count: CONTRACTS.reduce((s, g) => s + g.items.length, 0) },
  { id: 'reports', get label() { return i18n.t('ak_docspage_reporting'); }, Icon: FileBarChart, count: REPORTS.reduce((s, g) => s + g.items.length, 0) },
  { id: 'technical', get label() { return i18n.t('ak_docspage_technical_documentation'); }, Icon: Wrench, count: TECHNICAL.length },
  { id: 'legal', get label() { return i18n.t('ak_docspage_regulatory_acts'); }, Icon: Scale, count: LEGAL.reduce((s, g) => s + g.items.length, 0) },
];

/* ── Нормалізація ──────────────────────────────────────────────────────────
   Чотири реєстри зводяться до одного вигляду запису. У попередній версії
   кожен малювався по-своєму; з переглядачем це не працює — рядок списку має
   бути однаковим, а відмінності показує права панель. */

interface DocEntry {
  id: string;
  title: string;
  /** Реквізит: ким і коли затверджено, або форма звіту. */
  meta?: string;
  /** Періодичність — лише у звітності. */
  period?: string;
  /** Дата документа — з неї рахується рік для фільтра. */
  date?: string;
  /** Зовнішнє джерело (нормативні акти). */
  url?: string;
  /** Локальний файл. Поки що немає в жодного запису. */
  file?: string;
  /** Запис, якого ще не існує (договір не укладено). */
  pending?: boolean;
}

interface DocSection {
  label: string;
  Icon: LucideIcon;
  scope: DocScope;
  hint?: string;
  /**
   * Група без власного заголовка. Так зроблено на «Технічній документації»:
   * там група одна на всю вкладку, і її назвою була назва станції — та сама,
   * що вже стоїть у рядку «Реєстр …» над списком. Заголовок лише повторював
   * сказане рядком вище.
   */
  hideHead?: boolean;
  items: DocEntry[];
}

function sectionsFor(tab: TabId, stationName?: string): DocSection[] {
  if (tab === 'legal') {
    return LEGAL.map((g) => ({
      label: g.label, Icon: g.Icon, scope: g.scope,
      items: g.items.map((it) => ({
        id: `${g.label}/${it.title}`, title: it.title, meta: it.meta, url: it.url,
      })),
    }));
  }
  if (tab === 'contracts') {
    return CONTRACTS.map((g) => ({
      label: g.label, Icon: g.Icon, scope: g.scope,
      items: g.items.map((it) => ({
        id: `${g.label}/${it.title}`, title: it.title,
        meta: g.pending ? i18n.t('ak_docspage_not_yet_concluded') : it.meta,
        date: it.date,
        file: fileForStation(it.file, it.station, stationName),
        pending: g.pending || it.meta === i18n.t('ak_docspage_not_applicable'),
      })),
    }));
  }
  if (tab === 'reports') {
    return REPORTS.map((g) => ({
      label: g.label, Icon: g.Icon, scope: g.scope, hint: g.hint,
      items: g.items.map((it) => ({
        id: `${g.label}/${it.title}`, title: it.title, meta: it.form, period: it.period,
      })),
    }));
  }
  return [{
    label: stationName ?? i18n.t('ak_docspage_station_documents'),
    hideHead: true,
    Icon: Factory,
    scope: 'station',
    items: TECHNICAL.map((it) => ({
      id: `tech/${it.title}`,
      title: it.title,
      meta: it.meta,
      date: it.date,
      file: fileForStation(it.file, it.station, stationName),
    })),
  }];
}

/* ── Фільтр періоду ────────────────────────────────────────────────────────
   Рядки періодичності в даних неоднорідні: «місячна», «квартальна / річна»,
   «за подією». Тому зіставляємо за входженням, а не за рівністю — інакше
   «квартальна / річна» не потрапила б у жоден фільтр. */

type PeriodKey = 'all' | 'month' | 'quarter' | 'year';

const PERIOD_LABEL: Record<PeriodKey, string> = {
  get all() { return i18n.t('ak_docspage_all'); }, get month() { return i18n.t('ak_docspage_monthly'); }, get quarter() { return i18n.t('ak_docspage_quarterly'); }, get year() { return i18n.t('ak_docspage_annual'); },
};

const periodMatches = (key: PeriodKey, period?: string) => {
  if (key === 'all') return true;
  if (!period) return false;
  if (key === 'month') return period.includes(i18n.t('ak_docspage_monthly_2'));
  if (key === 'quarter') return period.includes(i18n.t('ak_docspage_quarterly_2'));
  return period.includes(i18n.t('ak_docspage_annual_2'));
};

/* ── Дрібні елементи ───────────────────────────────────────────────────────*/

/** Контрол кабінету: 32 / radius 8 / обводка --line-strong, без заливки. */
const CTRL: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  height: 32, borderRadius: 8, padding: '0 12px',
  background: 'transparent', border: '1px solid var(--line-strong)',
  color: 'var(--text-muted)', fontSize: 12, fontWeight: 600,
  cursor: 'pointer', whiteSpace: 'nowrap',
};

function SectionHead({ section }: { section: DocSection }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center" style={{
      gap: 10, padding: '14px 2px 8px', borderBottom: '1px solid var(--line)',
    }}>
      {/* Іконка більша за рядкову (20 проти 15) і тонша обведенням: у вузькій
          колонці заголовок групи інакше майже не відрізняється від назв
          документів під ним. Розмір тут працює як роздільник не гірше за
          лінію, і не додає сторінці ще однієї межі. */}
      <section.Icon size={20} strokeWidth={1.7} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
      <h3 className="font-display" style={{
        fontSize: 14, fontWeight: 600, color: 'var(--text)', lineHeight: 1.2,
      }}>{section.label}</h3>
      {section.hint && <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{section.hint}</span>}
      {/* Лічильник показує, скільки позицій групи справді мають документ.
          Коли всі — просто число; коли частина — «3 з 8», щоб обсяг реєстру
          не плутався з обсягом наявного. */}
      <span className="font-display tabular-nums" style={{
        marginLeft: 'auto', fontSize: 11.5, color: 'var(--text-dim)',
      }}>
        {(() => {
          const ok = section.items.filter(hasSource).length;
          return ok === section.items.length ? ok : t('ak_docspage_of', { ok, total: section.items.length });
        })()}
      </span>
    </div>
  );
}

/** Чи є що показати: локальний файл або зовнішнє джерело. */
const hasSource = (e: DocEntry) => !!(e.file || e.url);

/**
 * Рядок списку.
 *
 * АКТИВНИЙ ЛИШЕ ТОЙ, ЩО МАЄ ДЖЕРЕЛО. Доти всі 63 позиції реєстру були
 * однаково клікабельні, і різниця відкривалася аж у правій панелі —
 * заглушкою «файл ще не підвантажено». Тобто користувач мусив клацнути, щоб
 * дізнатися, що клацати не було сенсу.
 *
 * Тепер позиція без джерела — не кнопка взагалі: приглушений текст, звичайний
 * курсор, підпис «немає файла» праворуч. Вона лишається в переліку, бо
 * номенклатура має бути повною, але видно, що документа за нею немає.
 *
 * Станом на аудит: джерело мають 28 позицій із 63 — 21 нормативний акт
 * (посилання на zakon.rada.gov.ua, усі перевірені) і 7 файлів по Лучках.
 */
function DocRow({ entry, active, onSelect }: {
  entry: DocEntry; active: boolean; onSelect: () => void;
}) {
  const available = hasSource(entry);
  const { t } = useTranslation();

  const body = (
    <>
      <FileText size={15} strokeWidth={1.8}
        style={{
          color: available ? 'var(--text-dim)' : 'var(--line-strong)',
          flexShrink: 0, marginTop: 1,
        }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          display: 'block', fontSize: 12.5, lineHeight: 1.4, fontWeight: active ? 600 : 500,
          color: !available || entry.pending ? 'var(--text-dim)' : 'var(--text)',
        }}>{entry.title}</span>
        {(entry.meta || entry.date) && (
          <span style={{ display: 'block', fontSize: 10.5, color: 'var(--text-dim)', marginTop: 2 }}>
            {[entry.meta, docDateLabel(entry.date)].filter(Boolean).join(' · ')}
          </span>
        )}
      </span>
      {entry.period && (
        <span style={{
          fontSize: 10, color: 'var(--text-muted)', background: 'var(--surface-2)',
          borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap', flexShrink: 0,
        }}>{entry.period}</span>
      )}
      {available && entry.url && (
        <ExternalLink size={12} strokeWidth={2}
          style={{ color: 'var(--text-dim)', flexShrink: 0, marginTop: 2 }} />
      )}
      {!available && (
        <span style={{
          fontSize: 10, color: 'var(--text-dim)', whiteSpace: 'nowrap', flexShrink: 0, marginTop: 2,
        }}>{t('ak_docspage_no_file')}</span>
      )}
    </>
  );

  const base: React.CSSProperties = {
    gap: 10, padding: '10px 10px', borderRadius: 8, border: 'none',
  };

  if (!available) {
    return <div className="flex items-start" style={{ ...base, opacity: 0.7 }}>{body}</div>;
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex items-start w-full text-left cursor-pointer transition-colors"
      style={{
        ...base,
        // Активний рядок — нейтральна заливка й сіра риска ліворуч. На
        // референсі тут брендовий червоний, але §6 лишає для активного стану
        // сірий контур: червоний у кабінеті означає помилку.
        background: active ? 'var(--surface-2)' : 'transparent',
        boxShadow: active ? 'inset 2px 0 0 var(--text-muted)' : 'none',
      }}
    >
      {body}
    </button>
  );
}

/** Права панель: шапка документа плюс перегляд. */
function Viewer({ entry }: { entry: DocEntry | null }) {
  const { t } = useTranslation();
  // Стан завантаження джерела. Потрібен через зовнішні акти: zakon.rada.gov.ua
  // віддає першу відповідь приблизно за 4 секунди (заміряно), і весь цей час
  // кадр лишався білим — виглядало як зависання кабінету, хоча чекаємо ми
  // чужий сервер. Прискорити його не можемо, тож принаймні показуємо, що
  // саме відбувається і звідки тягнемо.
  const [loading, setLoading] = useState(false);
  const src = entry?.file ?? entry?.url;
  const embeddable = !!src && (!entry?.file || /\.pdf$/i.test(entry.file));

  useEffect(() => { setLoading(!!src && embeddable); }, [src, embeddable]);

  if (!entry) {
    return (
      <div className="flex flex-col items-center justify-center" style={{ gap: 8, height: '100%', padding: 24 }}>
        <Eye size={22} strokeWidth={1.6} style={{ color: 'var(--text-dim)' }} />
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('ak_docspage_select_a_document_from_the_list')}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col" style={{ height: '100%' }}>
      <div className="flex items-start" style={{
        gap: 12, padding: '14px 16px', borderBottom: '1px solid var(--line)',
      }}>
        <FileText size={20} strokeWidth={1.7} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="font-display" style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', lineHeight: 1.3 }}>
            {entry.title}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-dim)', marginTop: 3 }}>
            {[entry.meta, docDateLabel(entry.date), entry.period].filter(Boolean).join(' · ') || t('ak_docspage_details_not_specified')}
          </div>
        </div>

        {/* Дії доступні рівно настільки, наскільки є джерело. Кнопка, яка
            нікуди не веде, гірша за явно неактивну. */}
        {entry.file ? (
          <a href={entry.file} download style={{ ...CTRL, color: 'var(--text)', textDecoration: 'none' }}>
            <Download size={13} strokeWidth={2} />{t('ak_docspage_download')}
          </a>
        ) : entry.url ? (
          <a href={entry.url} target="_blank" rel="noopener noreferrer"
            style={{ ...CTRL, color: 'var(--text)', textDecoration: 'none' }}>
            <ExternalLink size={13} strokeWidth={2} />{t('ak_docspage_open_source')}
          </a>
        ) : (
          <span style={{ ...CTRL, opacity: 0.5, cursor: 'default' }}>
            <Download size={13} strokeWidth={2} />{t('ak_docspage_download')}
          </span>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, background: 'var(--surface-2)' }}>
        {entry.file && !/\.pdf$/i.test(entry.file) ? (
          // Таблиці й документи Office браузер у кадрі не показує — це не наше
          // обмеження, а відсутність вбудованого переглядача. Тому для них
          // лишається завантаження, і сказано це прямо.
          <div className="flex flex-col items-center justify-center" style={{ gap: 8, height: '100%', padding: 24 }}>
            <Download size={22} strokeWidth={1.6} style={{ color: 'var(--text-dim)' }} />
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('ak_docspage_preview_is_not_available_in_the_portal')}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-dim)', textAlign: 'center', maxWidth: 320 }}>
              {t('ak_docspage_the_browser_cannot_display_spreadsheet')}
            </div>
          </div>
        ) : entry.file || entry.url ? (
          // Джерело показуємо просто в кабінеті: zakon.rada.gov.ua не ставить
          // ні X-Frame-Options, ні CSP frame-ancestors, тож вбудовування
          // дозволене. Якщо колись заборонять — лишається кнопка «Відкрити
          // джерело» в шапці.
          <div style={{ position: 'relative', height: '100%' }}>
            <iframe
              key={entry.id}
              src={entry.file ?? entry.url}
              title={entry.title}
              onLoad={() => setLoading(false)}
              style={{ width: '100%', height: '100%', border: 'none', background: 'var(--surface-solid)' }}
            />
            {loading && (
              // Накриває кадр, поки чуже джерело не відповіло. Непрозора
              // підкладка — інакше крізь напис просвічував би білий кадр.
              <div className="flex flex-col items-center justify-center" style={{
                position: 'absolute', inset: 0, gap: 8, padding: 24,
                background: 'var(--surface-solid)',
              }}>
                <Loader2 size={20} strokeWidth={2}
                  className="animate-spin" style={{ color: 'var(--text-dim)' }} />
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                  {entry.url ? t('ak_docspage_loading_from_zakon_rada_gov_ua') : t('ak_docspage_opening_the_document')}
                </div>
                {entry.url && (
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', textAlign: 'center', maxWidth: 320 }}>
                    {t('ak_docspage_the_legislation_registry_pages_respond')}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center" style={{ gap: 8, height: '100%', padding: 24 }}>
            <FileText size={22} strokeWidth={1.6} style={{ color: 'var(--text-dim)' }} />
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('ak_docspage_file_not_uploaded_yet')}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-dim)', textAlign: 'center', maxWidth: 320 }}>
              {t('ak_docspage_the_registry_currently_contains_only_a')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Сторінка ──────────────────────────────────────────────────────────────*/

/**
 * Висота обох панелей — списку (ліворуч) і переглядача (праворуч). Одна
 * константа на двох, щоб вони не розходились: список скролиться всередині своєї
 * плитки, а не розтягує її. Рахується від верху вікна: над панелями лише хедер,
 * ряд вкладок і відступи.
 */
const PANEL_HEIGHT = 'calc(100vh - 218px)';
const PANEL_MIN_HEIGHT = 480;

export default function DocumentsHubPage() {
  const { t } = useTranslation();
  const { tab, setTab } = useDocsUi();
  const { selectedAsset } = useSelectedAsset();
  const stationName = selectedAsset?.assetName;

  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState<PeriodKey>('all');
  const [year, setYear] = useState<number | 'all'>('all');
  const [asc, setAsc] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Роки БУДУЮТЬСЯ З ДАНИХ, а не зашиті списком: коли договори отримають
  // дати, фільтр наповниться сам. Зараз у реєстрі немає жодної дати, тож
  // масив порожній — і це видно користувачеві, а не приховано.
  const years = useMemo(() => {
    const set = new Set<number>();
    CONTRACTS.forEach((g) => g.items.forEach((it) => {
      const y = docYear(it.date);
      if (y) set.add(y);
    }));
    return [...set].sort((a, b) => b - a);
  }, []);

  const sections = useMemo(() => {
    const base = sectionsFor(tab, stationName);
    const q = search.trim().toLowerCase();
    return base
      .map((s) => ({
        ...s,
        items: s.items
          .filter((it) => (tab === 'reports' ? periodMatches(period, it.period) : true))
          .filter((it) => (tab === 'contracts' && year !== 'all' ? docYear(it.date) === year : true))
          .filter((it) => !q
            || it.title.toLowerCase().includes(q)
            || (it.meta?.toLowerCase().includes(q) ?? false))
          .sort((a, b) => (asc ? 1 : -1) * a.title.localeCompare(b.title, i18n.language === 'en' ? 'en' : 'uk')),
      }))
      .filter((s) => s.items.length > 0);
  }, [tab, stationName, search, period, year, asc, i18n.language]);

  const flat = useMemo(() => sections.flatMap((s) => s.items), [sections]);
  // Вибір скидається, якщо запис утратив джерело — напр. перемкнули СЕС, і
  // файл Лучок більше не стосується обраної станції.
  const selected = flat.find((it) => it.id === selectedId && hasSource(it)) ?? null;

  // Лічильники біля фільтрів рахуємо до пошуку: інакше числа стрибали б на
  // кожну літеру й перестали б означати обсяг реєстру.
  const periodCounts = useMemo(() => {
    const all = sectionsFor(tab, stationName).flatMap((s) => s.items);
    return {
      all: all.length,
      month: all.filter((it) => periodMatches('month', it.period)).length,
      quarter: all.filter((it) => periodMatches('quarter', it.period)).length,
      year: all.filter((it) => periodMatches('year', it.period)).length,
    } as Record<PeriodKey, number>;
  }, [tab, stationName]);

  return (
    <DashboardLayout breadcrumb={[t('ak_docspage_documents')]}>
      <div className="min-h-screen -m-4 md:-m-6" style={{ background: 'var(--bg)' }}>
        <div className="px-4 md:px-6 pt-4 pb-8" style={{ fontFamily: 'Urbanist, Inter, sans-serif' }}>

          {/* РЯД ВКЛАДОК — над обома колонками, як ряд кнопок у «Керуванні» й
              «Фінансах»: від хедера 16px (pt-4 сторінки), до першої картки 20px
              (marginBottom). Раніше вкладки стояли в лівій колонці, а переглядач
              починався на їхньому рівні, тож відступи розходилися з рештою
              розділів.

              Кнопки лише з монохромною іконкою (iconOnly); підпис із лічильником
              лишається в нативній підказці й в aria-label. Назва активної вкладки
              стоїть у шапці плитки зі списком (праворуч від фільтрів). */}
          <div className="flex items-center" style={{ marginBottom: 20 }}>
            <SegmentedTabs
              variant="outline"
              iconOnly
              items={TABS.map((t) => ({ key: t.id, label: `${t.label} · ${t.count}`, Icon: t.Icon }))}
              activeKey={tab}
              onSelect={(key) => { setTab(key as TabId); setSelectedId(null); }}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(320px,460px)_1fr]" style={{ gap: 16, alignItems: 'start' }}>

            {/* ЛІВА КОЛОНКА: вкладки, фільтри, пошук, список. */}
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, height: PANEL_HEIGHT, minHeight: PANEL_MIN_HEIGHT }}>

            <div className="tile p-3 sm:p-4" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>

              {/* Шапка плитки: фільтри ліворуч, назва активної вкладки з лічильником
                  («Договори · 20») праворуч — на одному рівні з чипами «Усі роки /
                  2026». Чотири вкладки-іконки без слова не казали б, де ти, а
                  місце справа в цьому рядку інакше порожнє. */}
              <div className="flex items-start justify-between" style={{ gap: 12, marginBottom: (tab === 'reports' || tab === 'contracts') ? 0 : 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
              {tab === 'reports' && (
                <div className="flex flex-wrap" style={{ gap: 4, marginBottom: 10 }}>
                  {(['all', 'month', 'quarter', 'year'] as PeriodKey[]).map((k) => {
                    const on = period === k;
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setPeriod(k)}
                        style={{
                          ...CTRL,
                          height: 30, padding: '0 8px', gap: 4, fontSize: 11.5,
                          color: on ? 'var(--text)' : 'var(--text-muted)',
                          background: on ? 'color-mix(in srgb, var(--text) 6%, transparent)' : 'transparent',
                        }}
                      >
                        {PERIOD_LABEL[k]}
                        <span className="tabular-nums" style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>
                          {periodCounts[k]}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {tab === 'contracts' && (
                <div style={{ marginBottom: 10 }}>
                  <div className="flex flex-wrap" style={{ gap: 6 }}>
                    <button
                      type="button"
                      onClick={() => setYear('all')}
                      style={{
                        ...CTRL, height: 30, padding: '0 10px', fontSize: 11.5,
                        color: year === 'all' ? 'var(--text)' : 'var(--text-muted)',
                        background: year === 'all' ? 'color-mix(in srgb, var(--text) 6%, transparent)' : 'transparent',
                      }}
                    >
                      {t('ak_docspage_all_years')}
                    </button>
                    {years.map((y) => (
                      <button
                        key={y}
                        type="button"
                        onClick={() => setYear(y)}
                        className="tabular-nums"
                        style={{
                          ...CTRL, height: 30, padding: '0 10px', fontSize: 11.5,
                          color: year === y ? 'var(--text)' : 'var(--text-muted)',
                          background: year === y ? 'color-mix(in srgb, var(--text) 6%, transparent)' : 'transparent',
                        }}
                      >
                        {y}
                      </button>
                    ))}
                  </div>
                  {/* Порожній список років — не збій верстки, а стан даних.
                      Мовчазний контрол із однією кнопкою «Усі роки» виглядав би
                      як зламаний фільтр, тож стан названий уголос. */}
                  {years.length === 0 && (
                    <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 6 }}>
                      {t('ak_docspage_there_are_no_contract_dates_in_the_reg')}
                    </div>
                  )}
                </div>
              )}
                </div>
                <div className="flex items-baseline" style={{ gap: 8, flexShrink: 0, height: 30, alignItems: 'center' }}>
                  <span className="font-display" style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                    {TABS.find((t) => t.id === tab)?.label}
                  </span>
                  <span className="tabular-nums" style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>
                    {TABS.find((t) => t.id === tab)?.count}
                  </span>
                </div>
              </div>

              <div className="flex items-center" style={{ gap: 8, marginBottom: 10 }}>
                <div className="flex items-center" style={{
                  gap: 8, flex: 1, minWidth: 0, height: 32, padding: '0 10px',
                  borderRadius: 8, background: 'var(--surface-2)', border: '1px solid var(--line)',
                }}>
                  <Search size={14} strokeWidth={2} style={{ color: 'var(--text-dim)', flexShrink: 0 }} />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('ak_docspage_search_documents')}
                    className="outline-none"
                    style={{
                      flex: 1, minWidth: 0, background: 'transparent', border: 'none',
                      color: 'var(--text)', fontSize: 12.5,
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setAsc((v) => !v)}
                  title={asc ? t('ak_docspage_a_z') : t('ak_docspage_z_a')}
                  style={{ ...CTRL, width: 32, padding: 0 }}
                >
                  {asc ? <ArrowDownAZ size={14} strokeWidth={2} /> : <ArrowUpAZ size={14} strokeWidth={2} />}
                </button>
              </div>

              <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
                {sections.length === 0 ? (
                  <div style={{ fontSize: 12.5, color: 'var(--text-dim)', padding: '18px 2px' }}>
                    {t('ak_docspage_nothing_found')}
                  </div>
                ) : sections.map((s) => (
                  <section key={s.label}>
                    {!s.hideHead && <SectionHead section={s} />}
                    <div style={{ display: 'grid', gap: 2, padding: '6px 0 2px' }}>
                      {s.items.map((it) => (
                        <DocRow
                          key={it.id}
                          entry={it}
                          active={it.id === selectedId}
                          onSelect={() => setSelectedId(it.id)}
                        />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            </div>

            </div>

            {/* ПРАВА КОЛОНКА: перегляд. Висота рахується від верху сторінки —
                під нею лише заголовок і нижнє поле, тож документ видно на
                повний екран. */}
            <div className="tile" style={{
              padding: 0, overflow: 'hidden',
              height: PANEL_HEIGHT, minHeight: PANEL_MIN_HEIGHT,
            }}>
              <Viewer entry={selected} />
            </div>

          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
